import { useState, useEffect } from "react";
import { api } from "../App";
import { toast } from "sonner";
import { 
  Users, 
  Plus,
  Edit3,
  Trash2,
  Check,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";

// Source Form Dialog
const SourceFormDialog = ({ source, open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    handle: "",
    platform: "twitter",
    url: "",
    category: "",
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (source) {
      setFormData({
        name: source.name || "",
        handle: source.handle || "",
        platform: source.platform || "twitter",
        url: source.url || "",
        category: source.category || "",
        is_active: source.is_active !== false
      });
    } else {
      setFormData({
        name: "",
        handle: "",
        platform: "twitter",
        url: "",
        category: "",
        is_active: true
      });
    }
  }, [source]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      if (source) {
        await api.patch(`/sources/${source.id}`, formData);
        toast.success("Source updated");
      } else {
        await api.post('/sources', formData);
        toast.success("Source created");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save source");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border-[#2a2a3a] max-w-md" data-testid="source-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {source ? "Edit Source" : "Add Source"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Source name"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              data-testid="source-name"
            />
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Handle</Label>
            <Input
              value={formData.handle}
              onChange={(e) => setFormData({...formData, handle: e.target.value})}
              placeholder="@username"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              data-testid="source-handle"
            />
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Platform</Label>
            <Select 
              value={formData.platform} 
              onValueChange={(v) => setFormData({...formData, platform: v})}
            >
              <SelectTrigger className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]" data-testid="source-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12121a] border-[#2a2a3a]">
                <SelectItem value="twitter">Twitter/X</SelectItem>
                <SelectItem value="telegram">Telegram</SelectItem>
                <SelectItem value="rss">RSS</SelectItem>
                <SelectItem value="discord">Discord</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">URL</Label>
            <Input
              value={formData.url}
              onChange={(e) => setFormData({...formData, url: e.target.value})}
              placeholder="https://..."
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              data-testid="source-url"
            />
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Category</Label>
            <Input
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              placeholder="e.g., news, influencer, project"
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
              data-testid="source-category"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="font-mono text-xs text-[#7a7a8e]">Active</Label>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({...formData, is_active: v})}
              data-testid="source-active"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#7a7a8e]">Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className="bg-[#6ee7b7] text-[#0a0a0f] hover:bg-[#6ee7b7]/90"
            data-testid="source-save"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSource, setEditSource] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [filters, setFilters] = useState({
    platform: "",
    category: ""
  });

  const fetchSources = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.platform && filters.platform !== 'all') {
        params.append('platform', filters.platform);
      }
      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category);
      }
      
      const response = await api.get(`/sources?${params.toString()}`);
      setSources(response.data);
    } catch (error) {
      toast.error("Failed to load sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this source?")) return;
    
    try {
      await api.delete(`/sources/${id}`);
      toast.success("Source deleted");
      fetchSources();
    } catch (error) {
      toast.error("Failed to delete source");
    }
  };

  const handleToggleActive = async (source) => {
    try {
      await api.patch(`/sources/${source.id}`, { is_active: !source.is_active });
      toast.success(`Source ${source.is_active ? 'deactivated' : 'activated'}`);
      fetchSources();
    } catch (error) {
      toast.error("Failed to update source");
    }
  };

  // Get unique categories for filter
  const categories = [...new Set(sources.map(s => s.category).filter(Boolean))];

  return (
    <div data-testid="sources-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Sources</h1>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#6ee7b7] hover:bg-[#6ee7b7]/90 text-[#0a0a0f] font-semibold"
          data-testid="add-source"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Source
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-[#12121a] border-[#2a2a3a] mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="w-48">
              <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Platform</Label>
              <Select value={filters.platform} onValueChange={(v) => setFilters({...filters, platform: v})}>
                <SelectTrigger className="bg-[#1a1a26] border-[#2a2a3a]" data-testid="filter-platform">
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>
                <SelectContent className="bg-[#12121a] border-[#2a2a3a]">
                  <SelectItem value="all">All platforms</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="rss">RSS</SelectItem>
                  <SelectItem value="discord">Discord</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-48">
              <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Category</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                <SelectTrigger className="bg-[#1a1a26] border-[#2a2a3a]" data-testid="filter-category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-[#12121a] border-[#2a2a3a]">
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sources Table */}
      <Card className="bg-[#12121a] border-[#2a2a3a]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#7a7a8e]">Loading sources...</div>
            </div>
          ) : sources.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Users className="w-8 h-8 text-[#7a7a8e] mb-3" />
              <p className="text-[#7a7a8e]">No sources found</p>
              <Button
                variant="ghost"
                onClick={() => setShowAddDialog(true)}
                className="text-[#6ee7b7] mt-2"
              >
                Add your first source
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#2a2a3a] hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e]">Name</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e]">Handle</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e]">Platform</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e]">Category</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e]">Status</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-[#7a7a8e] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map(source => (
                  <TableRow 
                    key={source.id} 
                    className="border-[#2a2a3a] hover:bg-[#1a1a26]"
                    data-testid={`source-row-${source.id}`}
                  >
                    <TableCell className="font-medium text-[#e0e0e8]">{source.name}</TableCell>
                    <TableCell className="font-mono text-sm text-[#7a7a8e]">
                      {source.handle ? `@${source.handle}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className="font-mono text-[10px] bg-[#1a1a26] text-[#e0e0e8] border-[#2a2a3a] rounded uppercase">
                        {source.platform}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#7a7a8e]">{source.category || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`font-mono text-[10px] rounded ${
                        source.is_active 
                          ? 'bg-[#6ee7b7]/20 text-[#6ee7b7] border-[#6ee7b7]/30' 
                          : 'bg-[#7a7a8e]/20 text-[#7a7a8e] border-[#7a7a8e]/30'
                      }`}>
                        {source.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(source)}
                          className={`h-8 w-8 p-0 ${
                            source.is_active 
                              ? 'text-[#7a7a8e] hover:text-[#ef4444]' 
                              : 'text-[#7a7a8e] hover:text-[#6ee7b7]'
                          }`}
                          title={source.is_active ? 'Deactivate' : 'Activate'}
                          data-testid={`toggle-${source.id}`}
                        >
                          {source.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditSource(source)}
                          className="h-8 w-8 p-0 text-[#7a7a8e] hover:text-[#60a5fa]"
                          title="Edit"
                          data-testid={`edit-${source.id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(source.id)}
                          className="h-8 w-8 p-0 text-[#7a7a8e] hover:text-[#ef4444]"
                          title="Delete"
                          data-testid={`delete-${source.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <SourceFormDialog
        source={editSource}
        open={showAddDialog || !!editSource}
        onClose={() => {
          setShowAddDialog(false);
          setEditSource(null);
        }}
        onSave={fetchSources}
      />
    </div>
  );
};

export default SourcesPage;
